/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import { ErrorModel } from '../model/error.model';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';
import { ErrorSeverityEnum } from '../consts/error-severity.enum';

@Injectable()
export class ErrorService {
  constructor(private readonly translateService: TranslateService) {}

  getFormValidationErrors(abstractControls: AbstractControl): ErrorModel[] {
    const errors: ErrorModel[] = [];

    if (abstractControls.errors !== null) {
      errors.push(<ErrorModel>abstractControls.errors);
    }

    if (abstractControls instanceof UntypedFormGroup) {
      Object.keys(abstractControls.controls).forEach((keyError) => {
        errors.push(
          ...this.getFormValidationErrors(
            <AbstractControl>abstractControls.controls[keyError]
          )
        );
      });
    }
    return errors;
  }

  prepareErrorsForDisplay(
    serverError: any,
    translationPath : string,
    key?: string
  ): Observable<ToastMessageOptions[]> {
    const error = serverError ? serverError.error : null;

    if (error && error.fieldErrors && error.fieldErrors.length) {
      const errors: Observable<any>[] = [];

      error.fieldErrors.forEach((value: any) => {
        const translatedError = this.translateErrors(
          ErrorSeverityEnum.error,
          `${translationPath}.${value.message}`,
          value.message,
          key
        );
        errors.push(translatedError);
      });
      return combineLatest(errors);
    } else {
      const errors: any[] = [];

      if (error && error.description) {
        const err: ToastMessageOptions = {
          severity: ErrorSeverityEnum.error,
          summary: error.description,
          key: key ? key : '',
        };
        errors.push(err);
      }
      return of(errors);

    }

  }

  prepareTranslatableErrors(
    serverError: any,
    translationPath: string,
    key: string
  ): ToastMessageOptions[] {
    const error = serverError ? serverError.error : null;
    const errors: ToastMessageOptions[] = [];

    if (error && error.fieldErrors && error.fieldErrors.length) {
      error.fieldErrors.forEach((value: any) => {
        const err: ToastMessageOptions = {
          severity: ErrorSeverityEnum.error,
          summary: `${translationPath}.${value.message}`,
          data: { translatable: true },
          key: key,
        };
        errors.push(err);
      });
    } else {
      if (error && error.description) {
        const err: ToastMessageOptions = {
          severity: ErrorSeverityEnum.error,
          summary: error.description,
          key: key ? key : '',
        };
        errors.push(err);
      }
    }

    return errors;
  }

  translateErrors(
    severity: any,
    messageKey: string,
    defaultMessage?: string,
    key?: string
  ): Observable<ToastMessageOptions> {
    return this.translateService
      .get(messageKey, { defaultValue: defaultMessage })
      .pipe(
        map((res: string) => {
          return {
            severity: severity,
            summary: res,
            key: key ? key : '',
          };
        })
      );
  }
}
